import * as z from 'zod';
import { examItemEditorViewModelSchema } from '@/shared/api/generated';
import type { ZodShape } from '@/shared/api/generated/.kubb/ToZod';
import type { ExamItemEditorViewModel } from '@/shared/api/generated/types/ExamItemEditorViewModel';
import { requiresModelAnswer, supportsSubtype } from '@/shared/lib/examItemSubtype';

/**
 * Verifica se o HTML do enunciado tem conteúdo real: texto visível ou mídia
 * (imagem/tabela). Espelha a lógica de "vazio" do RichTextEditor para que um
 * enunciado só com imagem/tabela seja considerado preenchido.
 */
function hasRichTextContent(html: string): boolean {
  if (/<(img|table|iframe|video|audio|hr)\b/i.test(html)) return true;
  return (
    html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .trim().length > 0
  );
}

const questionFormBaseSchema = (
  examItemEditorViewModelSchema as unknown as z.ZodObject<ZodShape<ExamItemEditorViewModel>>
)
  .omit({ id: true })
  .extend({
    componentType: z
      .number({
        required_error: 'edit.settings.validation.componentTypeRequired',
        invalid_type_error: 'edit.settings.validation.componentTypeRequired',
      })
      .int()
      .positive({
        message: 'edit.settings.validation.componentTypeRequired',
      }),
    label: z.string().max(16000).refine(hasRichTextContent, {
      message: 'edit.settings.validation.labelRequired',
    }),
    level: z
      .number({
        required_error: 'edit.settings.validation.difficultyRequired',
        invalid_type_error: 'edit.settings.validation.difficultyRequired',
      })
      .int()
      .positive({
        message: 'edit.settings.validation.difficultyRequired',
      }),
  });

/**
 * Schema do formulário de questão.
 *
 * Derivado de `examItemEditorViewModelSchema` (gerado pelo Kubb) e refinado com
 * a regra condicional do subtipo dissertativo: os subtipos `ModelAnswerEssay` e
 * `MbaCase` exigem `modelAnswer`, que serve de referência para a correção por IA.
 */
export const questionFormSchema = questionFormBaseSchema.superRefine((values, ctx) => {
  if (!supportsSubtype(values.componentType) || !requiresModelAnswer(values.subtype)) return;

  if (!values.modelAnswer?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['modelAnswer'],
      message: 'edit.settings.validation.modelAnswerRequired',
    });
  }
});

export type QuestionEditFormValues = z.infer<typeof questionFormBaseSchema>;
